import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { ProductionApiError, previewFixedOrder, produceFixedOrder } from '../services/productionService';
import type {
  ProductionFixedOrder,
  ProductionFixedPreview,
  ProductionOrder,
  ProductionStockShortage,
} from '../types/production.types';
import { parseTrQuantity } from '../../../utils/numberFormat';
import { formatQty, formatQuantity, formatWarehouses } from '../utils/productionFormat';
import { formatRange, isOutsideRange, scaleExpectedRange } from '../utils/outputRange';

/** Kaç kez ondalık olabilir: 1,5 = bir buçuk parti (sunucu: 0'dan büyük, en fazla 50, 3 hane). */
const MAX_TIMES = 50;

/** Sunucudaki gibi: toplam = miktar × kez, 3 haneye yuvarlanır. */
export function fixedTotalQuantity(quantity: number, times: number): number {
  const raw = quantity * times;
  return Math.round((raw + Math.sign(raw) * Number.EPSILON) * 1000) / 1000;
}

export function parseTimes(text: string): number | null {
  const value = parseTrQuantity(text, 3);
  return Number.isFinite(value) && value > 0 && value <= MAX_TIMES ? value : null;
}

/** Çıkan miktar: Türkçe ondalık, 0'dan büyük, 3 hane. Geçersizse null. */
export function parseProduced(text: string): number | null {
  const value = parseTrQuantity(text, 3);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function formatInput(value: number): string {
  return formatQty(value).replace(/\s/g, '');
}

interface FixedProduceDialogProps {
  open: boolean;
  fixedOrder: ProductionFixedOrder | null;
  onClose: () => void;
  onProduced: (order: ProductionOrder) => void;
}

const FixedProduceDialog: React.FC<FixedProduceDialogProps> = ({ open, fixedOrder, onClose, onProduced }) => {
  const { t } = useTranslation('production');
  const [timesText, setTimesText] = useState('1');
  const [previewData, setPreviewData] = useState<ProductionFixedPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shortages, setShortages] = useState<ProductionStockShortage[]>([]);
  // Çıkan miktar: kullanıcı değiştirene kadar planlanan toplamı izler.
  const [producedText, setProducedText] = useState('');
  const [producedEdited, setProducedEdited] = useState(false);
  const [producedOpen, setProducedOpen] = useState(false);

  const times = parseTimes(timesText);
  const total = fixedOrder && times ? fixedTotalQuantity(fixedOrder.quantity, times) : null;
  const variable = Boolean(fixedOrder?.isVariableOutput);
  const produced = parseProduced(producedText);
  const producedDiffers = produced != null && total != null && Math.abs(produced - total) > 1e-9;
  const unit = fixedOrder?.unit || '';
  const label = fixedOrder?.name?.trim() || fixedOrder?.productName || '';

  useEffect(() => {
    if (!open) return;
    setTimesText('1');
    setPreviewData(null);
    setPreviewError(null);
    setSubmitError(null);
    setShortages([]);
    setProducedEdited(false);
    setProducedOpen(false);
  }, [open, fixedOrder]);

  useEffect(() => {
    if (!producedEdited) setProducedText(total != null ? formatInput(total) : '');
  }, [total, producedEdited]);

  // Kaç kez değişince malzeme listesi kısa bir beklemeyle yeniden hesaplanır.
  // Sabit fiş ucu kullanılır: yönetici yalnız "Sabit üretim yapabilir" ile de önizler.
  useEffect(() => {
    if (!open || !fixedOrder || times == null || total == null || total <= 0) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      previewFixedOrder(fixedOrder.id, times)
        .then((result) => {
          if (active) setPreviewData(result);
        })
        .catch((err) => {
          if (!active) return;
          setPreviewData(null);
          setPreviewError(err instanceof ProductionApiError ? err.message : t('fixedOrders.produce.previewFailed'));
        })
        .finally(() => {
          if (active) setPreviewLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, fixedOrder, times, total, t]);

  const lines = useMemo(() => previewData?.lines ?? [], [previewData]);
  // Eksi stoğa izin yoksa onay kapalı; izin varsa yalnız uyarı.
  const blocking = lines.filter((line) => line.isShort);
  const negativeWarnings = lines.filter((line) => !line.isShort && line.canBeNegative && line.available < line.plannedQuantity);

  // Beklenen aralık: önizleme (toplam için ölçeklenmiş) ya da reçete partisinden.
  const range = previewData && total != null && Math.abs((previewData.plannedQuantity ?? 0) - total) < 1e-9
    && (previewData.expectedProducedMin != null || previewData.expectedProducedMax != null)
    ? { min: previewData.expectedProducedMin ?? null, max: previewData.expectedProducedMax ?? null }
    : scaleExpectedRange(fixedOrder?.expectedOutputMin, fixedOrder?.expectedOutputMax, fixedOrder?.recipeOutputQuantity, total);
  const rangeText = formatRange(range, unit, { atLeast: t('complete.atLeast'), atMost: t('complete.atMost') });
  const outside = produced != null && isOutsideRange(produced, range);
  const showProduced = variable || producedOpen || producedDiffers;

  const canConfirm = Boolean(
    fixedOrder?.isActive
    && fixedOrder?.hasActiveRecipe
    && times
    && produced != null
    && previewData
    && !previewLoading
    && blocking.length === 0
    && !submitting,
  );

  const handleConfirm = async () => {
    if (!fixedOrder || !times) return;
    setSubmitting(true);
    setSubmitError(null);
    setShortages([]);
    try {
      const order = await produceFixedOrder(fixedOrder.id, {
        Times: times,
        ...(producedDiffers && produced != null ? { ProducedQuantity: produced } : {}),
      });
      onProduced(order);
      onClose();
    } catch (err) {
      if (err instanceof ProductionApiError) {
        setSubmitError(err.message || t('fixedOrders.produce.failed'));
        setShortages(err.shortages ?? []);
      } else {
        setSubmitError(t('fixedOrders.produce.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const status = (line: (typeof lines)[number]) => {
    if (line.isShort) return <Chip size="small" color="error" label={t('fixedOrders.produce.statusShort')} />;
    if (line.canBeNegative && line.available < line.plannedQuantity) {
      return <Chip size="small" color="warning" label={t('fixedOrders.produce.statusNegative')} />;
    }
    return <Chip size="small" color="success" variant="outlined" label={t('fixedOrders.produce.statusOk')} />;
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ flex: 1 }}>{t('fixedOrders.produce.title', { name: label })}</span>
        <GuideButton guideKey="production.fixedProduce" size="small" />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        {fixedOrder && (
          <Typography variant="body2" color="text.secondary">
            {formatWarehouses(fixedOrder.inputWarehouseName, fixedOrder.outputWarehouseName)}
            {fixedOrder.branchName ? ` · ${fixedOrder.branchName}` : ''}
          </Typography>
        )}
        {fixedOrder && !fixedOrder.hasActiveRecipe && (
          <Alert severity="error">{t('fixedOrders.noActiveRecipe')}</Alert>
        )}
        {fixedOrder && !fixedOrder.isActive && (
          <Alert severity="warning">{t('fixedOrders.inactiveCannotProduce')}</Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t('fixedOrders.produce.times')}
            value={timesText}
            onChange={(event) => setTimesText(event.target.value)}
            error={times == null}
            helperText={times == null ? t('fixedOrders.produce.timesInvalid', { max: MAX_TIMES }) : ' '}
            inputProps={{ inputMode: 'decimal', maxLength: 6 }}
            sx={{ width: 140 }}
            autoFocus
          />
          {fixedOrder && total != null && times != null && (
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              {t('fixedOrders.produce.totalLine', {
                quantity: formatQuantity(fixedOrder.quantity, unit),
                times: formatQuantity(times),
                total: formatQuantity(total, unit),
                product: fixedOrder.productName,
              })}
            </Typography>
          )}
        </Box>

        {showProduced ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label={t('fixedOrders.produce.produced')}
                value={producedText}
                onChange={(event) => {
                  setProducedText(event.target.value);
                  setProducedEdited(true);
                }}
                error={produced == null}
                helperText={produced == null
                  ? t('fixedOrders.produce.producedInvalid')
                  : (variable ? t('fixedOrders.produce.producedHelpVariable') : t('fixedOrders.produce.producedHelp'))}
                inputProps={{ inputMode: 'decimal', maxLength: 14 }}
                InputProps={{ endAdornment: unit ? <Typography variant="body2" color="text.secondary">{unit}</Typography> : undefined }}
                sx={{ width: { xs: '100%', sm: variable ? 320 : 260 } }}
                size={variable ? 'medium' : 'small'}
              />
              {producedEdited && producedDiffers ? (
                <Button size="small" onClick={() => setProducedEdited(false)}>{t('fixedOrders.produce.producedReset')}</Button>
              ) : null}
            </Box>
            {rangeText ? (
              <Typography variant="body2" color="text.secondary">{t('complete.expected', { range: rangeText })}</Typography>
            ) : null}
            {producedDiffers && total != null && produced != null ? (
              <Typography variant="body2">
                {t('fixedOrders.produce.producedLine', { planned: formatQuantity(total, unit), produced: formatQuantity(produced, unit) })}
              </Typography>
            ) : null}
            {outside && produced != null ? (
              <Alert severity="warning">{t('complete.rangeWarning', { produced: formatQuantity(produced, unit), range: rangeText })}</Alert>
            ) : null}
          </Box>
        ) : (
          <Box>
            <Button size="small" onClick={() => setProducedOpen(true)}>{t('fixedOrders.produce.producedToggle')}</Button>
          </Box>
        )}

        <Typography variant="subtitle2">{t('fixedOrders.produce.materials')}</Typography>
        {previewError && <Alert severity="error">{previewError}</Alert>}
        {previewLoading && !previewData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', opacity: previewLoading ? 0.6 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('fixedOrders.produce.material')}</TableCell>
                  <TableCell align="right">{t('fixedOrders.produce.required')}</TableCell>
                  <TableCell align="right">{t('fixedOrders.produce.available')}</TableCell>
                  <TableCell align="center">{t('fixedOrders.produce.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 && !previewLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">{t('fixedOrders.produce.noMaterials')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : lines.map((line) => (
                  <TableRow key={`${line.productId}-${line.sourcePath ?? ''}`}>
                    <TableCell>
                      <Typography variant="body2">{line.productName}</Typography>
                      {line.stockCode && <Typography variant="caption" color="text.secondary">{line.stockCode}</Typography>}
                    </TableCell>
                    <TableCell align="right">{formatQty(line.plannedQuantity)} {line.unit}</TableCell>
                    <TableCell align="right">{formatQty(line.available)} {line.unit}</TableCell>
                    <TableCell align="center">{status(line)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {blocking.length > 0 && (
          <Alert severity="error">
            {t('fixedOrders.produce.blocking', { names: blocking.map((line) => line.productName).join(', ') })}
          </Alert>
        )}
        {blocking.length === 0 && negativeWarnings.length > 0 && (
          <Alert severity="warning">
            {t('fixedOrders.produce.negativeWarning', { names: negativeWarnings.map((line) => line.productName).join(', ') })}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error">
            {submitError}
            {shortages.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {shortages.map((row) => (
                  <li key={`${row.productId}-${row.warehouseId}-${row.lotNo ?? ''}`}>
                    {t('fixedOrders.produce.shortageRow', {
                      name: row.productName,
                      required: formatQuantity(row.required, row.unit),
                      available: formatQuantity(row.available, row.unit),
                    })}
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{t('fixedOrders.cancel')}</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canConfirm}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : t('fixedOrders.produce.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixedProduceDialog;
